import React, { Fragment, useContext, useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import { ReportProblem, VisibilityOutlined } from "@mui/icons-material";
import { toast } from "react-toastify";
import { format, isSameDay, parseISO } from "date-fns";
import { getBackendUrl } from "../../config";
import {
  Avatar,
  CardHeader,
  Divider,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Typography,
  Card,
  Grid,
  Tooltip,
  CardContent,
  Box,
  IconButton,
  Badge,
  ListItemButton,
  alpha,
  useTheme
} from "@mui/material";

const backendUrl = getBackendUrl();

const MomentsUser = () => {
  const history = useHistory();
  const theme = useTheme();
  const { user, socket } = useContext(AuthContext);

  const [tickets, setTickets] = useState([]);
  const [update, setUpdate] = useState(false);
  const companyId = user.companyId;

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const { data } = await api.get("/usersMoments");
        if (mounted) {
          setTickets(data);
          setUpdate(!update);
        }
      } catch (err) {
        if (err.response?.status !== 500) {
          toastError(err);
        } else {
          toast.error(`${i18n.t("frontEndErrors.getUsers")}`);
        }
      }
    })();
    return () => { mounted = false; };
  }, []);

  useEffect(() => {
    const onAppMessage = (data) => {
      if (data.action === "create" || data.action === "update" || data.action === "delete") {
        (async () => {
          try {
            const { data } = await api.get("/usersMoments");
            setTickets(data);
            setUpdate(!update);
          } catch (err) {
            if (err.response?.status !== 500) {
              toastError(err);
            } else {
              toast.error(`${i18n.t("frontEndErrors.getUsers")}`);
            }
          }
        })();
      }
    }

    socket.on(`company-${companyId}-ticket`, onAppMessage)
    socket.on(`company-${companyId}-appMessage`, onAppMessage);

    return () => {
      socket.off(`company-${companyId}-ticket`, onAppMessage)
      socket.off(`company-${companyId}-appMessage`, onAppMessage);
    };
  }, [socket, companyId, update]);

  const Moments = useMemo(() => {
    if (tickets && tickets.length > 0) {
      const ticketsByUser = tickets.reduce((userTickets, ticket) => {
        const user = ticket.user;

        if (user) {
          const userIndex = userTickets.findIndex((group) => group.user.id === user.id);
          if (userIndex === -1) {
            userTickets.push({
              user,
              userTickets: [ticket],
            });
          } else {
            userTickets[userIndex].userTickets.push(ticket);
          }
        }
        return userTickets;
      }, []);

      return ticketsByUser.map((group, index) => (
        <Grid item xs={12} sm={6} md={4} lg={3} key={index}>
          <Card
            elevation={0}
            sx={{
              height: "400px",
              display: 'flex',
              flexDirection: 'column',
              borderRadius: "16px",
              boxShadow: "0 4px 6px rgba(0, 0, 0, 0.05)",
              border: `1px solid ${theme.palette.divider}`,
              transition: "all 0.3s ease",
              "&:hover": {
                transform: "translateY(-4px)",
                boxShadow: "0 10px 20px rgba(0,0,0,0.1)",
                borderColor: "primary.light"
              }
            }}
          >
            <CardHeader
              sx={{ 
                bgcolor: alpha(theme.palette.primary.main, 0.05), 
                borderBottom: `1px solid ${theme.palette.divider}`,
                py: 1.5
              }}
              avatar={
                <Avatar
                  sx={{ width: 44, height: 44, border: `2px solid ${theme.palette.background.paper}` }}
                  alt={`${group.user.profileImage}`}
                  src={group.user.profileImage ? `${backendUrl}/public/company${companyId}/user/${group.user.profileImage}` : null}
                />
              }
              title={<Typography variant="subtitle1" fontWeight="bold" color="primary">{group?.user?.name || "Pendentes"}</Typography>}
              subheader={
                <Typography variant="caption" color="textSecondary" fontWeight="500">
                  {group.userTickets?.length} atendimentos ativos
                </Typography>
              }
            />
            <CardContent sx={{
              p: 0,
              flex: 1,
              overflowY: 'auto',
              "&::-webkit-scrollbar": { width: '6px' },
              "&::-webkit-scrollbar-thumb": { backgroundColor: alpha(theme.palette.primary.main, 0.2), borderRadius: '10px' }
            }}>
              <List sx={{ p: 0 }}>
                {group.userTickets.map((ticket) => (
                  <Fragment key={ticket.id}>
                    <ListItemButton dense>
                      <ListItemAvatar>
                        <Avatar alt={`${ticket.contact.urlPicture}`} src={`${ticket.contact.urlPicture}`} />
                      </ListItemAvatar>
                      <ListItemText
                        disableTypography
                        primary={
                          <Typography variant="body1" noWrap color="textPrimary" fontWeight="500">
                            {ticket?.contact?.name}
                          </Typography>
                        }
                        secondary={
                          <Fragment>
                            <Typography noWrap variant="body2" color="textSecondary">
                              {ticket.lastMessage || "..."}
                            </Typography>
                            <Box component="span" sx={{
                              backgroundColor: ticket.queue?.color || "#7c7c7c",
                              color: "#FFF",
                              mr: 1,
                              px: 0.8,
                              py: 0.2,
                              fontWeight: 'bold',
                              borderRadius: 1,
                              fontSize: "0.7em",
                              display: 'inline-block',
                              mt: 0.5
                            }}>
                              {ticket.queue?.name.toUpperCase() || "SEM FILA"}
                            </Box>
                          </Fragment>
                        }
                      />
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', minWidth: '60px' }}>
                        <Typography
                          variant="caption"
                          sx={{
                            color: Number(ticket.unreadMessages) > 0 ? 'success.main' : 'text.secondary',
                            fontWeight: Number(ticket.unreadMessages) > 0 ? 'bold' : 'regular'
                          }}
                        >
                          {isSameDay(parseISO(ticket.updatedAt), new Date()) ? (
                            <>{format(parseISO(ticket.updatedAt), "HH:mm")}</>
                          ) : (
                            <>{format(parseISO(ticket.updatedAt), "dd/MM/yyyy")}</>
                          )}
                        </Typography>
                        {(user.profile === "admin" || ticket.userId === user.id) && (
                          <Tooltip title="Acessar Ticket">
                            <IconButton
                              size="small"
                              onClick={() => history.push(`/tickets/${ticket.uuid}`)}
                              sx={{ mt: 0.5, color: 'primary.main', bgcolor: alpha(theme.palette.primary.main, 0.1) }}
                            >
                              <VisibilityOutlined fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </ListItemButton>
                    <Divider variant="inset" component="li" />
                  </Fragment>
                ))}
              </List>
            </CardContent>
          </Card>
        </Grid>
      ));
    } else {
      return null;
    }
  }, [tickets, user, companyId, history, theme]);

  const MomentsPending = useMemo(() => {
    if (tickets && tickets.length > 0) {
      const pendingTickets = tickets.filter((ticket) => !ticket.user);
      if (pendingTickets.length === 0) return null;

      return (
        <Grid item xs={12} sm={6} md={4} lg={3}>
          <Card
            elevation={0}
            sx={{
              height: "400px",
              display: 'flex',
              flexDirection: 'column',
              borderRadius: "16px",
              boxShadow: "0 4px 6px rgba(0, 0, 0, 0.05)",
              border: `1px solid ${theme.palette.divider}`,
              transition: "all 0.3s ease",
              "&:hover": {
                transform: "translateY(-4px)",
                boxShadow: "0 10px 20px rgba(0,0,0,0.1)",
                borderColor: "warning.light"
              }
            }}
          >
            <CardHeader
              sx={{ 
                bgcolor: alpha(theme.palette.warning.main, 0.05), 
                borderBottom: `1px solid ${theme.palette.divider}`,
                py: 1.5
              }}
              avatar={<Avatar sx={{ bgcolor: alpha(theme.palette.warning.main, 0.2), color: 'warning.main' }}><ReportProblem /></Avatar>}
              title={
                <Typography variant="subtitle1" fontWeight="bold" color="warning.dark">
                  Pendentes
                </Typography>
              }
              subheader={
                <Typography variant="caption" color="textSecondary" fontWeight="500">
                  {pendingTickets?.length} aguardando
                </Typography>
              }
            />
            <CardContent sx={{
              p: 0,
              flex: 1,
              overflowY: 'auto',
              "&::-webkit-scrollbar": { width: '6px' },
              "&::-webkit-scrollbar-thumb": { backgroundColor: alpha(theme.palette.warning.main, 0.2), borderRadius: '10px' }
            }}>
              <List sx={{ p: 0 }}>
                {pendingTickets.map((ticket) => (
                  <Fragment key={ticket.id}>
                    <ListItemButton dense>
                      <ListItemAvatar>
                        <Avatar alt={`${ticket.contact.urlPicture}`} src={`${ticket.contact.urlPicture}`} />
                      </ListItemAvatar>
                      <ListItemText
                        disableTypography
                        primary={
                          <Typography variant="body1" noWrap color="textPrimary" fontWeight="500">
                            {ticket?.contact?.name}
                          </Typography>
                        }
                        secondary={
                          <Fragment>
                            <Typography noWrap variant="body2" color="textSecondary">
                              {ticket.lastMessage || "..."}
                            </Typography>
                            <Box component="span" sx={{
                              backgroundColor: ticket.queue?.color || "#7c7c7c",
                              color: "#FFF",
                              mr: 1,
                              px: 0.8,
                              py: 0.2,
                              fontWeight: 'bold',
                              borderRadius: 1,
                              fontSize: "0.7em",
                              display: 'inline-block',
                              mt: 0.5
                            }}>
                              {ticket.queue?.name.toUpperCase() || "SEM FILA"}
                            </Box>
                          </Fragment>
                        }
                      />
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', minWidth: '60px' }}>
                        <Typography
                          variant="caption"
                          sx={{
                            color: Number(ticket.unreadMessages) > 0 ? 'success.main' : 'text.secondary',
                            fontWeight: Number(ticket.unreadMessages) > 0 ? 'bold' : 'regular'
                          }}
                        >
                          {isSameDay(parseISO(ticket.updatedAt), new Date()) ? (
                            <>{format(parseISO(ticket.updatedAt), "HH:mm")}</>
                          ) : (
                            <>{format(parseISO(ticket.updatedAt), "dd/MM/yyyy")}</>
                          )}
                        </Typography>
                      </Box>
                    </ListItemButton>
                    <Divider variant="inset" component="li" />
                  </Fragment>
                ))}
              </List>
            </CardContent>
          </Card>
        </Grid>
      );
    } else {
      return null;
    }
  }, [tickets, theme]);

  return (
    <Fragment>
      <Grid container spacing={3}>
        {Moments}
        {MomentsPending}
      </Grid>
    </Fragment>
  );
};

export default MomentsUser;
